/**
 * Interaction Tracker Component
 * 
 * Tracks user interactions and app state changes.
 * This component should be rendered at the top level of the app.
 */

import React, { useEffect, useContext, useRef } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import interactionLedger from '../utils/interactionLedger';
import logger from '../logger';

const InteractionTracker = () => {
  const globalServices = useContext(GlobalStateContext);
  
  // Context should always be available since GlobalStateProvider wraps Routes
  // But guard against SSR or edge cases
  if (!globalServices?.appState) {
    return null;
  }
  
  // Hooks must be called unconditionally - service should always exist here
  const [appState, sendAppState] = useActor(globalServices.appState);
  const previousStateRef = useRef(null);
  const previousNoteIdRef = useRef(null);
  const previousUrlRef = useRef(null);

  useEffect(() => {
    if (!globalServices.appState) {
      return;
    }

    // Track xState state changes
    const subscription = globalServices.appState.subscribe((state) => {
      const currentState = state.context;
      const previousState = previousStateRef.current;

      // Track note selection changes
      if (currentState.selectedNoteId !== previousNoteIdRef.current) {
        interactionLedger.log(
          'noteSelection',
          'appState',
          {
            previousNoteId: previousNoteIdRef.current,
            currentNoteId: currentState.selectedNoteId,
            noteTitle: currentState.selectedNote?.note_title,
          },
          `Note selected: ${currentState.selectedNoteId || 'none'}`
        );
        previousNoteIdRef.current = currentState.selectedNoteId;
      }

      // Track state machine transitions
      if (previousState && state.value !== previousState.value) {
        interactionLedger.log(
          'stateTransition',
          'appState',
          {
            from: previousState.value,
            to: state.value,
            stateType: typeof state.value,
          },
          `State transition: ${JSON.stringify(previousState.value)} → ${JSON.stringify(state.value)}`
        );
      }

      // Track application priming
      if (currentState.applicationPrimed && (!previousState || !previousState.applicationPrimed)) {
        interactionLedger.log(
          'appPrimed',
          'appState',
          {
            selectedNoteId: currentState.selectedNoteId,
            selectedAofId: currentState.selectedAreaOfFocusId,
            selectedNotebookId: currentState.selectedNotebookId,
          },
          'Application primed and ready'
        );
      }

      previousStateRef.current = {
        value: state.value,
        context: { ...currentState },
      };
    });

    // Track URL changes
    const trackUrlChange = () => {
      const currentUrl = window.location.pathname + window.location.search;
      if (currentUrl !== previousUrlRef.current) {
        interactionLedger.log(
          'urlChange',
          'urlSync',
          {
            previousUrl: previousUrlRef.current,
            currentUrl,
            pathname: window.location.pathname,
            search: window.location.search,
          },
          `URL changed: ${previousUrlRef.current || '/'} → ${currentUrl}`
        );
        previousUrlRef.current = currentUrl;
      }
    };

    // Monitor URL changes via popstate and hashchange
    window.addEventListener('popstate', trackUrlChange);
    window.addEventListener('hashchange', trackUrlChange);

    // Initial URL
    previousUrlRef.current = window.location.pathname + window.location.search;

    // Track user interactions globally
    const trackClick = (event) => {
      const target = event.target;
      
      // Safely get className as string (handles DOMTokenList, string, or null/undefined)
      const classNameStr = target.className 
        ? (typeof target.className === 'string' 
            ? target.className 
            : target.className.toString())
        : '';
      const firstClass = classNameStr ? classNameStr.split(' ')[0] : '';
      
      const element = {
        tagName: target.tagName,
        id: target.id,
        className: classNameStr,
        textContent: target.textContent?.substring(0, 50),
        dataset: { ...target.dataset },
      };

      interactionLedger.log(
        'click',
        'userInteraction',
        {
          element,
          timestamp: Date.now(),
        },
        `User clicked: ${target.tagName}${target.id ? `#${target.id}` : ''}${firstClass ? `.${firstClass}` : ''}`
      );
    };

    const trackKeyPress = (event) => {
      // Only track significant key presses (not every character)
      if (event.key === 'Enter' || event.key === 'Escape' || event.key === 'Tab' || 
          (event.ctrlKey || event.metaKey) && event.key) {
        interactionLedger.log(
          'keyPress',
          'userInteraction',
          {
            key: event.key,
            code: event.code,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            target: {
              tagName: event.target?.tagName,
              id: event.target?.id,
              className: event.target?.className,
            },
          },
          `Key pressed: ${event.key}${event.ctrlKey || event.metaKey ? ' (with modifier)' : ''}`
        );
      }
    };

    // Add global event listeners
    document.addEventListener('click', trackClick, true); // Use capture phase
    document.addEventListener('keydown', trackKeyPress, true);

    // Track errors globally
    const originalError = window.onerror;
    const errorHandler = (message, source, lineno, colno, error) => {
      interactionLedger.log(
        'error',
        'error',
        {
          message,
          source,
          lineno,
          colno,
          error: error?.stack || error?.message,
        },
        `JavaScript error: ${message}`
      );
      if (originalError) {
        return originalError(message, source, lineno, colno, error);
      }
      return false;
    };
    window.onerror = errorHandler;

    // Track unhandled promise rejections
    const rejectionHandler = (event) => {
      interactionLedger.log(
        'unhandledRejection',
        'error',
        {
          reason: event.reason?.toString(),
          error: event.reason,
        },
        `Unhandled promise rejection: ${event.reason}`
      );
    };
    window.addEventListener('unhandledrejection', rejectionHandler);
    
    // Store handlers for cleanup
    const cleanupHandlers = {
      errorHandler,
      rejectionHandler,
      trackUrlChange,
      trackClick,
      trackKeyPress,
    };

    // Cleanup
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('popstate', cleanupHandlers.trackUrlChange);
      window.removeEventListener('hashchange', cleanupHandlers.trackUrlChange);
      document.removeEventListener('click', cleanupHandlers.trackClick, true);
      document.removeEventListener('keydown', cleanupHandlers.trackKeyPress, true);
      window.removeEventListener('unhandledrejection', cleanupHandlers.rejectionHandler);
      window.onerror = originalError;
    };
  }, [globalServices.appState, sendAppState]);

  // This component doesn't render anything
  return null;
};

export default InteractionTracker;

